import { mapElements } from "../../util/Utils";
import { copyLabels } from "../../tracking/FeatureTracking";
import { HeurekaElementFactory } from "../../util/HeurekaElementFactory";
import { UpdateReferencedAction } from "../UpdateReferencedAction";
import { events } from "../../events";
import { FilterTitle } from "../FilterTitle";

const SELECT_ALL_BUTTON_CLASS = "ts_heureka_selectAll";

const UNCHECKED_VISIBLE_VALUES = "li:not([hidden]) .js_hc_tValue:not([data-checked='true'])";
const CHECKED_VISIBLE_FACET_VALUES = "li:not([hidden]) .js_hc_tValue[data-checked='true']";
const ACTIVE_RANGES = ".hc_minMax [data-default-value]:not([data-default-value=''])";
const ACTIVE_VISIBLE_VALUES = `${CHECKED_VISIBLE_FACET_VALUES},${ACTIVE_RANGES}`;
const CUSTOM_RANGE_INPUT_CLASS = `hc_customRange__input`;

/*                                                                               */
function matchingIds(a: string, b: string): boolean {
  return a.includes(b) || b.includes(a);
}

export class SelectAllButton {
  static readonly factory = HeurekaElementFactory.byClass(SELECT_ALL_BUTTON_CLASS, SelectAllButton);

  /*               */

  constructor(
    readonly button: HTMLElement,
    readonly deselectMode = SelectAllButton.deselectMode(button.dataset.selectMode),
  ) {
    this.button = button;
    this.deselectMode = deselectMode;
  }

  private static deselectMode(mode?: string) {
    return mode === "deselect";
  }

  /*                                                */
  /*                                                                    */
  private resolveForm(): HTMLFormElement | null {
    const formId = this.button.getAttribute("form");
    const form = formId ? document.getElementById(formId) : null;
    return form instanceof HTMLFormElement ? form : null;
  }

  /*               */

  static initAll(rootElement?: ParentNode) {
    SelectAllButton.factory.forEach(SelectAllButton.init, rootElement);
  }

  private static init(elem: SelectAllButton) {
    elem.registerOnFormChange();
    elem.registerOnClick();
    elem.registerOnCustomRangeInput();
  }

  private registerOnFormChange() {
    const form = this.resolveForm();
    if (form) {
      const listener = this.formChanged.bind(this);
      const options = { passive: true };
      form.addEventListener("change", listener, options);
      form.addEventListener("input", listener, options);
      form.addEventListener("reset", listener, options);
    }
    return this;
  }

  private registerOnClick() {
    const form = this.resolveForm();
    this.button.addEventListener("click", this.clicked.bind(this));
    this.button.addEventListener("click", (event) => {
      event.target.dispatchEvent(
        new CustomEvent("heureka.removeAllTrashCan.clicked", { detail: { filterId: `${form.id}` }, bubbles: true }),
      );
      /*                                                                                      */
      this.hide();
      FilterTitle.factory.closest(this.button).validate();
    });
    return this;
  }

  /*                       */

  show() {
    this.button.hidden = false;
  }

  hide() {
    this.button.hidden = true;
  }

  toggleVisibility(force = !this.button.hidden) {
    this.button.hidden = force;
    return this;
  }

  toggleInputs() {
    return this.deselectMode ? this.deactivateActiveValues() : this.checkUncheckedValues();
  }

  isSelectAll() {
    return !this.deselectMode;
  }

  /*                 */

  private formChanged() {
    /*          */
    this.toggleVisibility(this.deselectMode != this.hasAnyVisibleActiveValue());
  }

  private clicked(event: Event) {
    const changedItems = this.toggleInputs().map(copyLabels.bind(this, this.button)).length;
    if (changedItems) {
      this.triggerChange();
    }
    event.stopPropagation();
  }

  /*       */

  /*                                                     */

  private hasAnyVisibleActiveValue(form: HTMLFormElement | null = this.resolveForm()) {
    return !!form?.querySelector(ACTIVE_VISIBLE_VALUES);
  }

  private checkUncheckedValues(form: HTMLFormElement | null = this.resolveForm()) {
    return mapElements<HTMLInputElement, HTMLInputElement>(
      UNCHECKED_VISIBLE_VALUES,
      (elem) => {
        elem.checked = true;
        UpdateReferencedAction.updateAllValues(elem);
        return elem;
      },
      form || undefined,
    );
  }

  private deactivateActiveValues(form: HTMLFormElement | null = this.resolveForm()) {
    return mapElements<HTMLInputElement, HTMLInputElement>(
      ACTIVE_VISIBLE_VALUES,
      (elem) => {
        return SelectAllButton.clearInput(elem);
      },
      form || undefined,
    );
  }

  private triggerChange(form: HTMLFormElement | null = this.resolveForm()) {
    form?.dispatchEvent(new Event("change", { bubbles: true }));
  }

  private static clearInput(elem: HTMLInputElement): HTMLInputElement {
    /*                                                                                                   */
    if (elem.classList.contains(CUSTOM_RANGE_INPUT_CLASS) && "value" in elem) {
      elem.value = "_,_";
      return elem;
    }

    if ("checked" in elem) {
      elem.checked = false;
      UpdateReferencedAction.updateAllValues(elem);
    }

    return elem;
  }

  /*                                                      */

  private static clearInputDeprecated(elem: HTMLInputElement): HTMLInputElement {
    const type = elem.type ? elem.type : elem.getAttribute("type");
    /*                                                                                                   */
    if (elem.classList.contains(CUSTOM_RANGE_INPUT_CLASS) && "value" in elem) {
      elem.value = "_,_";
      return elem;
    }

    switch (type) {
      case "text":
        elem.value = "_,_";
        break;
      case "radio":
      case "checkbox":
        elem.checked = false;
        UpdateReferencedAction.updateAllValues(elem);
        break;
    }
    return elem;
  }

  private registerOnCustomRangeInput() {
    document.addEventListener("heureka.customRange.onInput", (event) => {
      const customEvent = event as CustomEvent<{
        filterId: string;
      }>;
      const eventFilterId = customEvent.detail?.filterId;
      const form = this.resolveForm();
      /*                                                           */
      if (eventFilterId && form && matchingIds(form.id, eventFilterId)) {
        this.show();
      }
    });
  }
}

function onFiltersLoaded() {
  SelectAllButton.initAll();
}

export function registerFilterFormSelectAllButtons() {
  events.onFilterSectionLoaded(onFiltersLoaded);
}
